import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons";
import React from "react";

import AboutMe, { AboutMePlaceholder } from "@/mk2/apps/blogs/components/AboutMe";
import AddProductsBanner from "@/mk2/apps/blogs/components/AddProductsBanner";
import MyProducts, { Option } from "@/mk2/apps/blogs/components/MyProducts";
import ProfileIsBlocked from "@/mk2/apps/blogs/components/ProfileIsBlocked";
import ProfileTabs, { Tab } from "@/mk2/apps/blogs/components/ProfileTabs";
import { checkIfmoreAboutMeHasContent } from "@/mk2/apps/blogs/containers/ProfileDetail/ProfileDetail.helpers";
import { homepageUrl } from "@/mk2/apps/home/urls";
import { dummyPhoto } from "@/mk2/apps/strollers/constants";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { Failure } from "@/mk2/components/errors/Failure";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { cacheLast } from "@/mk2/helpers/cache";
import { LoadingState, isSuccess } from "@/mk2/reducers/loadingState";
import { ProfileProduct, ProfileProductType } from "@/mk2/schemas";
import {
    BACK_TO_HOMEPAGE,
    PROFILE_PRODUCTS_TITLE,
    USER_STROLLERS_MY_STROLLERS,
} from "@/mk/autogenerated/translation";
import { isAuthenticatedAs } from "@/mk/bazaar/common/userUtils";

import styles from "./ProfileProducts.mscss";
import { Props } from "./ProfileProductsPage";

class ProfileProducts extends React.Component<Props> {
    private profileProducts = cacheLast<ProfileProduct[]>(true);
    private filteredSuggestedStrollerOptions = cacheLast<Option[]>(true);

    public render() {
        const {
            isMobile,
            detailLoadingState,
            profileDetailData,
            requestUser,
            requestUserPermissions,
            match: {
                params: { username },
            },
            suggestedStrollerOptions,
            addingLoadingState,
            location,
        } = this.props;

        const isAdmin = requestUserPermissions?.indexOf("photoblog.can_manage_blogs") >= 0;
        const isOwner = profileDetailData.user
            ? isAuthenticatedAs(requestUser, profileDetailData.user)
            : false;

        if (profileDetailData.user?.isBlocked) {
            return (
                <ProfileIsBlocked
                    username={username}
                    isAdmin={isAdmin}
                    isBlockedSelf={profileDetailData.user.isBlockedSelf}
                    isLegacyBlocked={profileDetailData.isLegacyBlocked}
                    isLiked={profileDetailData.userIsLiked}
                    onUnlike={this.onUnlike}
                />
            );
        }

        const profileProducts = this.profileProducts(
            () =>
                // TODO: Rewrite to be more generic when we start to display more products
                profileDetailData.userStrollers.map((stroller): ProfileProduct => {
                    const photo = stroller.previewPhoto ?? dummyPhoto;
                    const review = profileDetailData.userStrollersReviews.find(
                        (r) => r.strollerId === stroller.id,
                    );

                    return {
                        type: ProfileProductType.STROLLER,
                        id: stroller.id,
                        slug: stroller.slug,
                        name: stroller.name,
                        imageUrl: photo.url250x250,
                        imageWidth: photo.originalWidth,
                        imageHeight: photo.originalHeight,
                        reviewId: review?.id,
                        reviewerScore: review?.score,
                        reviewStatus: review?.status,
                    };
                }),
            profileDetailData.userStrollers,
            profileDetailData.user,
            profileDetailData.userStrollersReviews,
        );

        const filteredSuggestedStrollerOptions = this.filteredSuggestedStrollerOptions(
            () =>
                suggestedStrollerOptions.filter(
                    (option) => !profileDetailData.userStrollers.some((s) => s.id === option.value),
                ),
            suggestedStrollerOptions,
            profileDetailData.userStrollers,
        );

        return (
            <>
                {profileProducts.length > 0 && isSuccess(detailLoadingState) && (
                    <h1 className={styles.ProfileProducts__title}>{PROFILE_PRODUCTS_TITLE}</h1>
                )}

                {isMobile && (
                    <LoadingSwitch
                        loadingState={detailLoadingState}
                        onLoad={this.loadDetail}
                        onRenderInit={this.renderInitDetail}
                        onRenderSuccess={this.renderLoadSuccessDetail}
                        onRenderFailure={this.renderFailureDetail}
                        hasData={!!profileDetailData.user}
                    />
                )}

                {profileProducts.length === 0 && isSuccess(detailLoadingState) && (
                    <AddProductsBanner className={styles.ProfileProducts__banner} />
                )}

                <MyProducts
                    className={styles.ProfileProducts__products}
                    title={USER_STROLLERS_MY_STROLLERS}
                    products={profileProducts}
                    onRemove={this.onRemove}
                    canManage={isOwner || isAdmin}
                    canReview={isOwner}
                    options={filteredSuggestedStrollerOptions}
                    onAdd={this.onAddStroller}
                    onSearchOptions={this.onSearchStrollerOptions}
                    isAdding={addingLoadingState === LoadingState.LOADING}
                    pathname={location.pathname}
                />
            </>
        );
    }

    private loadDetail = () => {
        const {
            match: {
                params: { username },
            },
            onLoadProfileDetail,
        } = this.props;
        onLoadProfileDetail(username);
    };

    private renderInitDetail = () => {
        const {
            isMobile,
            match: {
                params: { username },
            },
        } = this.props;

        return <AboutMePlaceholder isMobile={isMobile} username={username} />;
    };

    private renderLoadSuccessDetail = () => {
        const {
            isMobile,
            location,
            onDeleteAvatarByAdmin,
            onSetLikeUser,
            profileDetailData,
            requestUser,
            requestUserPermissions,
            match: {
                params: { username },
            },
        } = this.props;

        const moreAboutMeHasContent = checkIfmoreAboutMeHasContent(
            isMobile,
            profileDetailData,
            requestUser,
            requestUserPermissions,
        );

        return (
            <React.Fragment>
                <AboutMe
                    isMobile={isMobile}
                    location={location}
                    moreAboutMeHasContent={moreAboutMeHasContent}
                    onDeleteAvatarByAdmin={onDeleteAvatarByAdmin}
                    onSetLikeUser={onSetLikeUser}
                    profileDetailData={profileDetailData}
                    requestUser={requestUser}
                    requestUserPermissions={requestUserPermissions}
                />
                <ProfileTabs
                    username={username}
                    active={Tab.PRODUCTS}
                    postsCount={
                        profileDetailData.userStats.blogsAlbumsCount +
                        profileDetailData.userStats.blogsArticlesCount +
                        profileDetailData.userStats.blogsShortMessagesCount
                    }
                    productsCount={
                        // TODO: Rewrite to be more generic when we start to display more products
                        profileDetailData.userStrollers.length
                    }
                />
            </React.Fragment>
        );
    };

    private renderFailureDetail = () => {
        const { staticContext, detailErrorMessage } = this.props;

        return (
            <Failure
                errorMessage={detailErrorMessage}
                staticContext={staticContext}
                backButton={
                    <Btn
                        type={BtnType.Blue}
                        label={BACK_TO_HOMEPAGE}
                        icon={faArrowLeft}
                        link={homepageUrl}
                    />
                }
                onRetry={this.loadDetail}
            />
        );
    };

    private onUnlike = () => {
        const { profileDetailData, onSetLikeUser } = this.props;
        if (profileDetailData?.user) {
            onSetLikeUser(profileDetailData.user.username, false);
        }
    };

    private onRemove = (product: ProfileProduct) => {
        const { onRemoveProduct, profileDetailData } = this.props;
        onRemoveProduct(profileDetailData.user.username, product.type, product.id);
    };

    private onAddStroller = (value: Option["value"]) => {
        const { onAddProduct, profileDetailData } = this.props;
        onAddProduct(profileDetailData.user.username, ProfileProductType.STROLLER, value as number);
    };

    private onSearchStrollerOptions = (term: string) => {
        const { onSearchProducts } = this.props;
        onSearchProducts(ProfileProductType.STROLLER, term);
    };
}

export default ProfileProducts;
